"use client";

import React, { useState, useEffect, useCallback, memo } from "react";
import AsyncCreatableSelect from "react-select/async-creatable";
import AsyncSelect from "react-select/async";

import { Controller, useFormContext } from "react-hook-form";
import { encryptDataNetwork } from "@/utils/encryptor";

interface OptionType {
  label: string;
  value: string;
}

interface FormAjaxProps {
  label?: string;
  formFieldClassName?: string;
  name: string;
  defaultValue?: {
    value: any | any[];
    dataKey?: string;
    getOptions: (id?: string) => Promise<any>;
  };
  debounceTime?: number;
  getOptions: (inputValue: string) => Promise<any>;
  error?: string;
  disabledCreateNew?: boolean;
  required?: boolean;
  labelKey?: string;
  valueKey?: string;
  dataKey?: string;
  dependencies?: string | string[] | boolean;
  additionalParams?: object;
  isMulti?: boolean; // <-- add this
  required_asterik?: boolean;
}

const Select: React.FC<FormAjaxProps> = memo(
  ({
    label,
    name,
    defaultValue,
    formFieldClassName,
    disabledCreateNew,
    debounceTime = 500,
    required_asterik,
    additionalParams = {},
    getOptions,
    error,
    required = false,
    labelKey = "label",
    valueKey = "value",
    dataKey = "data",
    isMulti = false, // <-- default is false
  }) => {
    const {
      control,
      setValue,
      formState: { errors },
    } = useFormContext();

    const [isLoading, setIsLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState<any>(
      isMulti ? [] : null
    );

    function debounce(fn: Function, delay: number) {
      let timeout: NodeJS.Timeout;
      return (...args: any[]) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), delay);
      };
    }

    const debouncedLoad = useCallback(
      debounce(
        async (inputValue: string, callback: (options: any[]) => void) => {
          setIsLoading(true);
          try {
            const response = await getOptions(
              await encryptDataNetwork({
                search: inputValue,
                limit: 50,
                paginate: true,
                ...additionalParams,
              })
            );

            const items = (
              dataKey ? (response.data as any)?.[dataKey] || [] : response
            ) as any[];

            const options = items.map((item: any) => ({
              label: item[labelKey],
              value: item[valueKey],
            }));

            callback(options);
          } catch (error) {
            console.error("Error fetching options:", error);
            callback([]);
          } finally {
            setIsLoading(false);
          }
        },
        debounceTime
      ),
      [debounceTime, getOptions, labelKey, valueKey, dataKey]
    );

    const loadOptions = (
      inputValue: string,
      callback: (options: any[]) => void
    ) => {
      debouncedLoad(inputValue, callback);
    };

    useEffect(() => {
      if (defaultValue?.value) {
        const fetchDefaultOption = async () => {
          try {
            const response = await getOptions(
              await encryptDataNetwork({
                search: "",
                limit: 50,
                paginate: true,
              })
            );

            const items = (
              dataKey ? (response.data as any)?.[dataKey] || [] : response
            ) as any[];

            if (isMulti) {
              let foundOptions = items
                .filter((item: any) =>
                  defaultValue?.value.includes(item[valueKey])
                )
                .map((item: any) => ({
                  label: item[labelKey],
                  value: item[valueKey],
                }));

              // Jika ga sama jumlah antara default value sama yang ditemuin di all lists
              if (foundOptions.length != defaultValue.value.length) {
                foundOptions = [];
              }

              if (foundOptions.length == 0) {
                await Promise.all(
                  defaultValue.value?.map(async (i: string) => {
                    const result = await defaultValue.getOptions(i);
                    if (result.data) {
                      foundOptions.push(
                        defaultValue.dataKey &&
                          (result.data as any)?.[defaultValue.dataKey]
                          ? (result.data as any)?.[defaultValue.dataKey] || null
                          : (result?.data as any)
                      );
                    }
                  })
                );

                foundOptions = foundOptions.map((item: any) => ({
                  label: item[labelKey],
                  value: item[valueKey],
                }));
              }
              if (foundOptions.length == 0) {
                foundOptions = defaultValue.value.map((item: any) => ({
                  label: item,
                  value: item,
                }));
              }
              setSelectedOption(foundOptions);
            } else {
              let found = items.find((item: any) => {
                return item[valueKey] == defaultValue;
              });

              if (!found) {
                const result = await defaultValue.getOptions();
                if (defaultValue?.dataKey == "account") {
                  console.log({ result });
                }
                if (result.data) {
                  found = (
                    defaultValue.dataKey
                      ? (result.data as any)?.[defaultValue.dataKey] || null
                      : result?.data
                  ) as any;
                }
              }
              if (!found) {
                found = {
                  label: defaultValue.value,
                  value: defaultValue.value,
                };
                setSelectedOption(found);
              } else {
                setSelectedOption(
                  found
                    ? {
                        label: found[labelKey]?.toString(),
                        value: found[valueKey]?.toString(),
                      }
                    : null
                );
              }
            }
          } catch (err) {
            console.error("Error fetching default option:", err);
          }
        };

        fetchDefaultOption();
        const currentValue = control._formValues?.[name];
        if (!currentValue || currentValue.length === 0) {
          setValue(name, defaultValue?.value?.toString());
        }
      }
    }, [defaultValue?.value]);

    const SelectComponent = disabledCreateNew
      ? AsyncSelect
      : AsyncCreatableSelect;

    return (
      <div
        className={`w-full max-lg:flex-col flex lg:items-center gap-2 ${formFieldClassName}`}
      >
        {label && (
          <label className="block text-[0.6rem] font-medium w-full lg:w-[200px]">
            {required || required_asterik ? (
              <span className="text-error">*</span>
            ) : (
              ""
            )}{" "}
            {label}
          </label>
        )}

        <div className="flex flex-col gap-2 relative w-full lg:flex-1">
          <div className="relative w-full form-ajax">
            <Controller
              name={name}
              control={control}
              rules={{ required }}
              render={({ field }) => (
                <SelectComponent
                  cacheOptions
                  loadOptions={loadOptions}
                  defaultOptions
                  styles={{
                    placeholder: (base) => ({
                      ...base,
                      textTransform: "uppercase",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      textTransform: "uppercase",
                    }),
                  }}
                  isClearable
                  required={required}
                  isLoading={isLoading}
                  isMulti={isMulti}
                  value={selectedOption}
                  className="react-select border-primary"
                  classNames={{
                    control: ({ isFocused }) =>
                      `!border !border-primary !bg-base-100 !min-h-[24px] !rounded-[0.5rem] !px-2`,
                    container: () => "!min-h-[24px] !bg-base-100",
                    valueContainer: (props) =>
                      `!min-h-[24px] !p-0 ${
                        props.hasValue ? "!font-bold !text-primary" : ""
                      }`,
                    input: () => "!p-0 !m-0 leading-[24px]",
                    indicatorsContainer: () => "!min-h-[24px]",
                    dropdownIndicator: () => "!p-0 !m-0",
                    clearIndicator: () => "!p-0 !m-0",
                  }}
                  getOptionLabel={(option: any) => option.label}
                  getOptionValue={(option: any) => option.value}
                  onChange={(option: any) => {
                    setSelectedOption(option);
                    if (isMulti) {
                      const values =
                        option?.map((opt: any) => opt.value?.toString()) ?? [];
                      field.onChange(values);
                    } else {
                      field.onChange(option ? option.value?.toString() : null);
                    }
                  }}
                  onCreateOption={
                    disabledCreateNew
                      ? undefined
                      : (inputValue: string) => {
                          const newOption: OptionType = {
                            label: inputValue,
                            value: inputValue,
                          };
                          if (isMulti) {
                            const current =
                              (selectedOption as OptionType[]) ?? [];
                            const newOptions = [...current, newOption];
                            setSelectedOption(newOptions);
                            field.onChange(newOptions.map((opt) => opt.value));
                          } else {
                            setSelectedOption(newOption);
                            field.onChange(newOption.value);
                          }
                        }
                  }
                  onInputChange={(inputValue) => inputValue}
                />
              )}
            />
          </div>

          {error && <p className="text-error text-xs mt-1">{error}</p>}
        </div>
      </div>
    );
  }
);
Select.displayName = "Select";

const FormAjax: React.FC<FormAjaxProps> = (params) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(false);
    setTimeout(() => {
      setReady(true);
    }, 300);
  }, [params.dependencies]);

  return ready ? <Select {...params} /> : <></>;
};

export default FormAjax;
